import { ActionIcon, Menu, Text, useMantineTheme } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import {
  IconDotsVertical,
  IconEdit,
  IconTrash,
  IconSend,
} from "@tabler/icons-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import type { SupplierDto } from "~/gen/api/types"
import { TransferContactModal } from "~/pages/Brand/Suppliers/List/components/TransferContactModal.tsx"
import DeleteContact from "~/pages/Brand/Suppliers/SuppliersRepository/components/DeleteContact"
import {
  calculateRemainingCertificateRequests,
  calculateRemainingInformationRequests,
  convertContactForTransferModal,
} from "~/pages/Brand/Suppliers/SuppliersRepository/utils/contact-utils"

type SupplierActionsMenuProps = {
  id: string
  onUpdate: () => void
  contact: SupplierDto
  canDelete?: boolean
}

export const SupplierActionsMenu: React.FC<SupplierActionsMenuProps> = ({
  id,
  onUpdate,
  contact,
  canDelete = true,
}: SupplierActionsMenuProps) => {
  const { t } = useTranslation("suppliers")
  const theme = useMantineTheme()
  const [isDeleteModalOpen, deleteModalActions] = useDisclosure(false)
  const [isTransferModalOpen, transferModalHandlers] = useDisclosure(false)
  const [transferType, setTransferType] = useState<
    "informationsRequests" | "certiticates"
  >("informationsRequests")

  const remainingInformationRequests = calculateRemainingInformationRequests(
    contact.informationRequestsCount as Record<string, number>
  )
  const remainingCertificateRequests = calculateRemainingCertificateRequests(
    contact.certificateRequestsCount as Record<string, number>
  )

  const isDirect = contact.isDirectSupplier
  const canTransferInformationRequests =
    remainingInformationRequests > 0 && isDirect
  const canTransferCertificates = remainingCertificateRequests > 0 && isDirect

  const contactForModal = convertContactForTransferModal(contact)
  const handleTransferInformationRequests = () => {
    setTransferType("informationsRequests")
    transferModalHandlers.open()
  }

  const handleTransferCertificates = () => {
    setTransferType("certiticates")
    transferModalHandlers.open()
  }

  return (
    <>
      <Menu shadow="md" width={200}>
        <Menu.Target>
          <ActionIcon variant="ghost">
            <IconDotsVertical
              size={theme.other.icon.size}
              stroke={theme.other.icon.stroke}
            />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item
            leftSection={
              <IconEdit
                size={theme.other.icon.size}
                stroke={theme.other.icon.stroke}
              />
            }
            onClick={onUpdate}
          >
            <Text>{t("repository.actions.edit")}</Text>
          </Menu.Item>
          {canTransferInformationRequests && (
            <Menu.Item
              leftSection={
                <IconSend
                  size={theme.other.icon.size}
                  stroke={theme.other.icon.stroke}
                />
              }
              onClick={handleTransferInformationRequests}
            >
              <Text>
                {t("supplier.transferContact", {
                  count: remainingInformationRequests,
                })}
              </Text>
            </Menu.Item>
          )}
          {canTransferCertificates && (
            <Menu.Item
              leftSection={
                <IconSend
                  size={theme.other.icon.size}
                  stroke={theme.other.icon.stroke}
                />
              }
              onClick={handleTransferCertificates}
            >
              <Text>
                {t("supplier.transferContactCertificates", {
                  count: remainingCertificateRequests,
                })}
              </Text>
            </Menu.Item>
          )}
          {canDelete ? (
            <Menu.Item
              leftSection={
                <IconTrash
                  size={theme.other.icon.size}
                  stroke={theme.other.icon.stroke}
                />
              }
              onClick={deleteModalActions.open}
            >
              <Text>{t("repository.actions.delete")}</Text>
            </Menu.Item>
          ) : null}
        </Menu.Dropdown>
      </Menu>
      <DeleteContact
        id={id}
        isDeleteModalOpen={isDeleteModalOpen}
        deleteModalActions={deleteModalActions}
      />
      {isTransferModalOpen && (
        <TransferContactModal
          opened={isTransferModalOpen}
          closeModal={transferModalHandlers.close}
          contact={contactForModal}
          type={transferType}
        />
      )}
    </>
  )
}
